import { Box, Checkbox, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  BuildChecklistEntity,
  ChecklistItemEntity,
  PreferenceCardEntity,
} from '@peritasai/repository';
import React from 'react';

const SuppliesListContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const SuppliesGroupContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const SuppliesGroupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const SuppliesItemContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'disabled',
})<{ isSelected?: boolean; disabled?: boolean }>(
  ({ theme, isSelected, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${
      isSelected ? theme.palette.primary.main : theme.palette.divider
    }`,
    backgroundColor: isSelected
      ? theme.palette.primary.light + '10'
      : theme.palette.background.paper,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: disabled
        ? theme.palette.divider
        : theme.palette.primary.main,
      backgroundColor: disabled
        ? theme.palette.action.disabledBackground
        : theme.palette.action.hover,
    },
    opacity: disabled ? 0.5 : 1,
  })
);

const SuppliesItemInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: 4,
});

const SuppliesItemLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
}));

const SuppliesItemDetails = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 1,
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontWeight: 600,
  marginBottom: theme.spacing(2),
}));

const NO_OP_ITEM_SELECT = (
  _checklistItem: ChecklistItemEntity
): Promise<void> => Promise.resolve();

type PreferenceUntrackedAsset =
  PreferenceCardEntity['untrackedAssets']['supplies'][number];

interface ItemListProps {
  preferenceItems: PreferenceUntrackedAsset[];
  checklistItemsByCatalogId: Map<string, ChecklistItemEntity[]>;
  onItemSelect: (checklistItem: ChecklistItemEntity) => Promise<void>;
  isLoading: boolean;
}

interface SuppliesSelectionStepProps {
  preferenceCard: PreferenceCardEntity;
  buildChecklist: BuildChecklistEntity;
  onSingleInstrumentSelect: (
    checklistItem: ChecklistItemEntity
  ) => Promise<void>;
  onInstrumentSelect?: (checklistItem: ChecklistItemEntity) => Promise<void>;
  onSupplySelect: (checklistItem: ChecklistItemEntity) => Promise<void>;
  isLoading?: boolean;
}

const ItemList: React.FC<ItemListProps> = ({
  preferenceItems,
  checklistItemsByCatalogId,
  onItemSelect,
  isLoading,
}) => {
  const handleItemClick = async (checklistItem?: ChecklistItemEntity) => {
    if (isLoading || !checklistItem) return;
    await onItemSelect(checklistItem);
  };

  if (preferenceItems.length === 0) {
    return (
      <Box sx={{ padding: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No items available
        </Typography>
      </Box>
    );
  }

  return (
    <SuppliesListContainer>
      {preferenceItems.map((prefItem) => {
        const checklistItems =
          checklistItemsByCatalogId.get(prefItem.catalogId) ?? [];
        const rowCount = Math.max(
          prefItem.quantity || 1,
          checklistItems.length || 0,
          1
        );

        return (
          <SuppliesGroupContainer key={prefItem.id}>
            <SuppliesGroupHeader>
              <Typography variant="h6" fontWeight={600}>
                {prefItem.name}
              </Typography>
              {prefItem.productNumber && (
                <Typography variant="caption" color="text.secondary">
                  Product: {prefItem.productNumber}
                  {prefItem.manufacturer && ` • ${prefItem.manufacturer}`}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Qty Required: {prefItem.quantity}
              </Typography>
            </SuppliesGroupHeader>
            {Array.from({ length: rowCount }).map((_, index) => {
              const checklistItem = checklistItems[index];
              const isSelected = checklistItem?.allocated ?? false;
              const isDisabled = !checklistItem;

              return (
                <SuppliesItemContainer
                  key={`${prefItem.id}-${index}`}
                  isSelected={isSelected}
                  disabled={isDisabled}
                  onClick={() => handleItemClick(checklistItem)}
                >
                  <SuppliesItemInfo>
                    <SuppliesItemLabel>
                      {prefItem.name} #{index + 1}
                    </SuppliesItemLabel>
                    {checklistItem?.notes && (
                      <SuppliesItemDetails>
                        {checklistItem.notes}
                      </SuppliesItemDetails>
                    )}
                    {isDisabled && (
                      <SuppliesItemDetails>
                        Checklist item not available yet
                      </SuppliesItemDetails>
                    )}
                  </SuppliesItemInfo>
                  <Checkbox
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => handleItemClick(checklistItem)}
                    color="primary"
                    onClick={(e) => e.stopPropagation()}
                  />
                </SuppliesItemContainer>
              );
            })}
          </SuppliesGroupContainer>
        );
      })}
    </SuppliesListContainer>
  );
};

export const SuppliesSelectionStep: React.FC<SuppliesSelectionStepProps> = ({
  preferenceCard,
  buildChecklist,
  onSingleInstrumentSelect,
  onInstrumentSelect,
  onSupplySelect,
  isLoading = false,
}) => {
  const singleInstruments =
    preferenceCard.untrackedAssets.singlePackedInstruments;
  const instruments = preferenceCard.untrackedAssets.instruments ?? [];
  const supplies = preferenceCard.untrackedAssets.supplies;

  const singleInstrumentChecklistMap = React.useMemo(() => {
    const map = new Map<string, ChecklistItemEntity[]>();
    buildChecklist.getSinglePackedInstruments().forEach((group) => {
      map.set(group.catalogItemId, group.items);
    });
    return map;
  }, [buildChecklist]);

  const instrumentChecklistMap = React.useMemo(() => {
    const map = new Map<string, ChecklistItemEntity[]>();
    buildChecklist.getUntrackedInstruments().forEach((group) => {
      map.set(group.catalogItemId, group.items);
    });
    return map;
  }, [buildChecklist]);

  const suppliesChecklistMap = React.useMemo(() => {
    const map = new Map<string, ChecklistItemEntity[]>();
    buildChecklist.getSupplies().forEach((group) => {
      map.set(group.catalogItemId, group.items);
    });
    return map;
  }, [buildChecklist]);

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoading && (
        <LoadingOverlay>
          <CircularProgress size={40} />
        </LoadingOverlay>
      )}

      {/* Single Instruments Section */}
      <SectionContainer>
        <SectionTitle>Single Instruments</SectionTitle>
        <ItemList
          preferenceItems={singleInstruments as PreferenceUntrackedAsset[]}
          checklistItemsByCatalogId={singleInstrumentChecklistMap}
          onItemSelect={onSingleInstrumentSelect}
          isLoading={isLoading}
        />
      </SectionContainer>

      {/* Instruments Section */}
      <SectionContainer>
        <SectionTitle>Instruments</SectionTitle>
        <ItemList
          preferenceItems={instruments as PreferenceUntrackedAsset[]}
          checklistItemsByCatalogId={instrumentChecklistMap}
          onItemSelect={onInstrumentSelect ?? NO_OP_ITEM_SELECT}
          isLoading={isLoading}
        />
      </SectionContainer>

      {/* Supplies Section */}
      <SectionContainer>
        <SectionTitle>Supplies</SectionTitle>
        <ItemList
          preferenceItems={supplies as PreferenceUntrackedAsset[]}
          checklistItemsByCatalogId={suppliesChecklistMap}
          onItemSelect={onSupplySelect}
          isLoading={isLoading}
        />
      </SectionContainer>
    </Box>
  );
};
